import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { BottomNav } from "@/components/BottomNav";
import { MbCard } from "@/components/MbCard";
import { MbBadgeSun } from "@/components/MbBadgeSun";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { isWithinWindow, formatCountdown, getWindowLabel, getLocalDateString, TimeWindow } from "@/lib/time-utils";
import { checkCurrentUserSubscription } from "@/lib/subscription-utils";
import { Play, Lock, Clock, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { VideoPlayerDialog } from "@/components/VideoPlayerDialog";
import { AppFooter } from "@/components/AppFooter";

interface Session {
  id: string;
  title_gu: string;
  title_hi: string | null;
  description_gu: string;
  description_hi: string | null;
  yt_video_id: string;
  video_language: 'gu' | 'hi';
  date: string;
  windows: TimeWindow[];
  notes: string;
  thumbnail_url: string | null;
  thumbnail_active: boolean;
}

export default function Today() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [countdowns, setCountdowns] = useState<Record<string, string>>({});
  const [playingVideo, setPlayingVideo] = useState<{ videoId: string; title: string } | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logo, strings, language } = useLanguage();

  const getSessionContent = (session: Session) => {
    if (language === 'hi') {
      return {
        title: session.title_hi || session.title_gu,
        description: session.description_hi || session.description_gu,
      };
    }
    return {
      title: session.title_gu,
      description: session.description_gu,
    };
  };

  useEffect(() => {
    // Set up auth listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
        if (!session) {
          navigate("/auth");
        }
      }
    );

    // Then check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session) {
        navigate("/auth");
      } else {
        fetchTodaySession();
        checkSubscription();
      }
    });

    return () => subscription.unsubscribe();
  }, [language]);

  const checkSubscription = async () => {
    setCheckingSubscription(true);
    const hasSubscription = await checkCurrentUserSubscription();
    setHasActiveSubscription(hasSubscription);
    setCheckingSubscription(false);
  };

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('sessions-today')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sessions',
          filter: 'status=eq.published'
        },
        (payload) => {
          console.log('Session change detected:', payload);
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            toast({
              title: strings.newLessonAdded,
              description: strings.newLessonAvailable,
            });
          }
          fetchTodaySession();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  useEffect(() => {
    // Update countdowns every second
    const timer = setInterval(() => {
      const newCountdowns: Record<string, string> = {};
      sessions.forEach((session) => {
        const access = isWithinWindow(session.date, session.windows);
        if (!access.allowed && access.nextWindow) {
          newCountdowns[session.id] = formatCountdown(access.nextWindow);
        }
      });
      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(timer);
  }, [sessions]);

  const fetchTodaySession = async () => {
    try {
      const today = getLocalDateString();
      
      const { data, error } = await supabase
        .from("sessions")
        .select("*")
        .eq("date", today)
        .eq("status", "published")
        .eq("video_language", language)
        .order("created_at", { ascending: true });

      if (error) {
        throw error;
      }

    if (data && data.length > 0) {
      setSessions(data.map(session => ({
        ...session,
        video_language: (session.video_language as 'gu' | 'hi'),
        windows: (session.windows as unknown as TimeWindow[]) || [],
      })));
    } else {
      setSessions([]);
    }
    } catch (error: any) {
      console.error("Error fetching session:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = async (session: Session, currentWindow: TimeWindow) => {
    // Check subscription before allowing play
    if (!hasActiveSubscription) {
      toast({
        title: strings.subscriptionRequired,
        description: strings.subscriptionRequiredDesc,
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("auth_user_id", user.id)
        .single();

      if (profile) {
        await supabase.from("plays").insert({
          user_id: profile.id,
          session_id: session.id,
          window_label: currentWindow?.label || "custom",
        });
      }

      // Open video in secure dialog instead of new tab
      const content = getSessionContent(session);
      setPlayingVideo({
        videoId: session.yt_video_id,
        title: content.title,
      });
    } catch (error: any) {
      toast({
        title: strings.error,
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (authLoading || loading || checkingSubscription) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20">
        <div className="animate-pulse text-muted-foreground">{strings.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] pb-20">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="દમયંતી બાગ" className="h-10" />
              <h1 className="text-xl font-bold text-primary">{strings.todayLesson}</h1>
            </div>
            <MbBadgeSun>{strings.today}</MbBadgeSun>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-4">
        {sessions.length > 0 ? (
          sessions.map((session) => {
            const accessStatus = isWithinWindow(session.date, session.windows);
            const countdown = countdowns[session.id] ?? "";
            const content = getSessionContent(session);

            return (
              <MbCard key={session.id} elevated className="overflow-hidden">
                <div className="relative aspect-video bg-muted">
                  {session.thumbnail_active && session.thumbnail_url ? (
                    <>
                      <img
                        src={session.thumbnail_url}
                        alt={content.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-center justify-center">
                        <div className="text-center text-white p-6">
                          <div className="text-4xl mb-3">🎥</div>
                          <h3 className="text-2xl font-bold mb-2">Upcoming Session</h3>
                          <p className="text-sm opacity-90">Stay Tuned for the Release</p>
                        </div>
                      </div>
                    </>
                  ) : session.yt_video_id ? (
                    <>
                      <img
                        src={`https://img.youtube.com/vi/${session.yt_video_id}/maxresdefault.jpg`}
                        alt={content.title}
                        className="w-full h-full object-cover"
                      />
                      {!accessStatus.allowed && (
                        <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                          <Lock className="w-16 h-16 text-white/80" />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-muted">
                      <Calendar className="w-16 h-16 text-muted-foreground" />
                    </div>
                  )}
                </div>

                <div className="p-6 space-y-4">
                  <h2 className="text-2xl font-bold text-foreground">{content.title}</h2>
                  
                  {content.description && (
                    <p className="text-muted-foreground">{content.description}</p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {session.windows.map((window, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "px-3 py-1 rounded-full text-sm font-medium border-2",
                          accessStatus.allowed && accessStatus.currentWindow?.label === window.label
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-muted text-muted-foreground border-border"
                        )}
                      >
                        <Clock className="w-3 h-3 inline mr-1" />
                        {getWindowLabel(window.label)}: {window.from} - {window.to}
                      </div>
                    ))}
                  </div>

                  {accessStatus.allowed && hasActiveSubscription ? (
                    <Button
                      variant="hero"
                      size="lg"
                      className="w-full"
                      onClick={() => handlePlay(session, accessStatus.currentWindow!)}
                    >
                      <Play className="mr-2" />
                      {strings.playNow}
                    </Button>
                  ) : !hasActiveSubscription ? (
                    <div className="space-y-4">
                      <div className="bg-destructive/10 border-2 border-destructive/20 p-6 rounded-lg text-center space-y-3">
                        <Lock className="w-12 h-12 mx-auto text-destructive" />
                        <div>
                          <h3 className="font-bold text-lg text-foreground mb-2">
                            🔒 {strings.subscriptionRequired}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            {strings.subscriptionRequiredDesc}
                          </p>
                          <p className="text-sm font-semibold text-foreground mb-4">
                            {strings.subscriptionRequiredMessage}
                          </p>
                          <ul className="text-left text-sm space-y-2 mb-4 mx-auto max-w-xs">
                            {strings.subscriptionBenefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-primary mt-0.5">✓</span>
                                <span className="text-muted-foreground">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <Button
                          variant="default"
                          size="lg"
                          className="w-full"
                          onClick={() => navigate("/subscribe")}
                        >
                          {strings.subscribeToWatch}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-muted p-4 rounded-lg text-center">
                        <Lock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium text-foreground mb-1">
                          {strings.lockedWindow}
                        </p>
                        {accessStatus.nextWindow && (
                          <p className="text-xs text-muted-foreground">
                            {strings.startsIn} {countdown}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {session.notes && (
                    <div className="bg-accent/20 p-4 rounded-lg">
                      <p className="text-sm text-accent-foreground">{session.notes}</p>
                    </div>
                  )}
                </div>
              </MbCard>
            );
          })
        ) : (
          <MbCard className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-bold mb-2">{strings.noLessonsToday}</h2>
            <p className="text-muted-foreground">{strings.waitForNextLesson}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/upcoming")}
            >
              {strings.viewUpcoming}
            </Button>
          </MbCard>
        )}
      </div>

      <VideoPlayerDialog
        videoId={playingVideo?.videoId || null}
        isOpen={!!playingVideo}
        onClose={() => setPlayingVideo(null)}
        sessionTitle={playingVideo?.title}
      />

      <div className="pb-16">
        <AppFooter />
      </div>

      <BottomNav />
    </div>
  );
}
